import React, { useEffect, useState } from 'react';

import { MtxIcon } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import Notification from '@/components/Notification/Notification';
import { URLS } from '@/constants/urls';
import { STATUS } from '@/enums/status';

import styles from './styles.module.css';

const COPIED_RESET_DELAY_MS = 3000;
const COPIED_TOAST_ID = 'qps-email-copied';

const copyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    const tempInput = document.createElement('input');
    tempInput.value = value;
    document.body.appendChild(tempInput);
    tempInput.select();
    const isCopied = document.execCommand('copy'); // NOSONAR typescript:S1874 -- intentional fallback for browsers without Clipboard API support
    tempInput.remove();
    // Le repli renvoie false quand rien n'a été copié : ne pas laisser annoncer un succès.
    if (!isCopied) throw new Error('Copy to clipboard failed');
  }
};

// Bandeau haut de viewport du node 58-7070. Pattern du repo (`useToastNotifications`) :
// `toast.custom` + `Notification`, le `<Toaster/>` vivant dans `MainLayout`.
const showCopiedToast = (label: string) => {
  toast.custom(
    (toastId) => (
      <Notification
        title={label}
        message=""
        status={STATUS.SUCCESS}
        duration={COPIED_RESET_DELAY_MS}
        onClose={() => toast.dismiss(toastId)}
      />
    ),
    { id: COPIED_TOAST_ID, duration: Infinity, unstyled: true }
  );
};

interface ContentModalQpsContactProps {
  onRequestClose: () => void;
  onChannelSelect: (channel: 'contact_chat' | 'contact_telephone' | 'contact_email') => void;
}

const ContentModalQpsContact: React.FC<ContentModalQpsContactProps> = ({ onRequestClose, onChannelSelect }) => {
  const { t } = useTranslation();
  const [isEmailCopied, setIsEmailCopied] = useState(false);
  const phoneNumber = t('navigation.phoneNumber');

  useEffect(() => {
    if (!isEmailCopied) return undefined;
    const timeout = setTimeout(() => setIsEmailCopied(false), COPIED_RESET_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [isEmailCopied]);

  return (
    <div className={styles.content}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('qps.contactModal.title')}</h2>
        <p className={styles.subtitle}>{t('qps.contactModal.subtitle')}</p>
      </div>

      <ul className={styles.rows}>
        {/* Classe globale : le script iAdvize cible cette entrée par son nom pour l'afficher quand
            le chat est disponible. Pas de jumeau `card-button-chat-indisponible` : chat absent =
            ligne absente. */}
        <li>
          <button
            type="button"
            className={`card-button-chat-disponible ${styles.row}`}
            style={{ display: 'none' }}
            onClick={() => {
              onChannelSelect('contact_chat');
              onRequestClose();
            }}
          >
            <span aria-hidden="true">
              <MtxIcon name="chat-3" size={24} />
            </span>
            <span className={styles.rowLabel}>{t('qps.contactModal.chat')}</span>
            <span aria-hidden="true" className={styles.trailingIcon}>
              <MtxIcon name="chevron-right" size={24} />
            </span>
          </button>
        </li>

        <li>
          <a
            className={styles.row}
            href={`tel:${phoneNumber.replaceAll(' ', '')}`}
            onClick={() => onChannelSelect('contact_telephone')}
          >
            <span aria-hidden="true">
              <MtxIcon name="phonecall-outcoming" size={24} />
            </span>
            <span className={styles.rowLabel}>{phoneNumber}</span>
            <span aria-hidden="true" className={styles.trailingIcon}>
              <MtxIcon name="chevron-right" size={24} />
            </span>
          </a>
        </li>

        <li className={styles.row}>
          <span aria-hidden="true">
            <MtxIcon name="mail" size={24} />
          </span>
          <a
            className={`${styles.rowLabel} ${styles.rowLink}`}
            href={`mailto:${URLS.MTP_EMAIL}`}
            onClick={() => onChannelSelect('contact_email')}
          >
            {URLS.MTP_EMAIL}
          </a>
          <button
            type="button"
            className={styles.copyButton}
            aria-label={t('qps.contactModal.copyEmailLabel')}
            onClick={() => {
              onChannelSelect('contact_email');
              void copyToClipboard(URLS.MTP_EMAIL)
                .then(() => {
                  setIsEmailCopied(true);
                  showCopiedToast(t('qps.contactModal.emailCopied'));
                })
                .catch(() => undefined);
            }}
          >
            <MtxIcon name={isEmailCopied ? 'check' : 'copy'} size={24} />
          </button>
        </li>
      </ul>
    </div>
  );
};

export default ContentModalQpsContact;
